import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ToastType = 'success' | 'error';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
}

interface ToastContextType {
    showToast: (message: string, type: ToastType) => void;
    toasts: Toast[];
    removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const showToast = useCallback(
        (message: string, type: ToastType) => {
            const id = Math.random().toString(36).substring(2, 9);
            const newToast: Toast = { id, message, type };

            setToasts((prev) => [...prev, newToast]);

            setTimeout(() => {
                removeToast(id);
            }, 5000);
        },
        [removeToast]
    );

    const value: ToastContextType = useMemo(
        () => ({ showToast, toasts, removeToast }),
        [showToast, toasts, removeToast]
    );

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
};
